// O perfil na tela (espec. 8.3): tabela `parcela × competência × defasagem × percentual`,
// botão de nova linha, soma ao vivo e a mensagem do validador. A competência de cada linha
// sai do início do novo cronograma (a defasagem conta dali). É SEMPRE ela que mostra o perfil — os atalhos
// só a preenchem e o "Personalizado" a torna editável. Apresentacional: as linhas
// chegam prontas, com o `lineId` que serve de chave.
import { allocateCents } from "./allocate";
import { lineCompetences } from "./engine";
import { bpsToPercentInput, competenceText, integerInput, percentInputToBps, percentLabel } from "./format";
import { AT_SIGHT_PROFILE, MAX_LINES, offsetRangeFor } from "./profile";
import { TargetValue } from "./RuleTargetPicker";

// O erro do perfil descreve os campos de percentual (espec. 8.5) — este id é o alvo
// do `aria-describedby` daqui e do `RuleShortcutFields`.
export const PROFILE_ERROR_ID = "ampli-rule-editor-profile-error";

export const UNIT_OPTIONS = Object.freeze([
  { value: "months", label: "Meses" },
  { value: "calendar_days", label: "Dias corridos" },
  { value: "business_days", label: "Dias úteis" },
]);
const UNIT_LABEL_BY_VALUE = Object.fromEntries(UNIT_OPTIONS.map((option) => [option.value, option.label]));
export const unitLabel = (unit) => UNIT_LABEL_BY_VALUE[unit] ?? UNIT_LABEL_BY_VALUE.months;

/**
 * As linhas editáveis carregam um id gerado, estável e único, usado como chave do React.
 * Nem o índice do array nem a defasagem servem: a tabela do "Personalizado" aceita
 * duplicata transitória de defasagem enquanto o cliente digita, e remover/reordenar
 * linhas com chave por posição faz o React reaproveitar o input errado.
 *
 * A forma interna da linha é sempre `{ lineId, unit, offset, percentBps }` — a mesma para
 * as três unidades — para que a tabela nunca precise ramificar por forma. `unit` "months"
 * com `offset` é aceito por `validateProfile` (profile.js) exatamente como o `offsetMonths`
 * legado, então esta forma interna vai direto ao validador sem conversão.
 */
let lineSequence = 0;
export const withLineId = (line) => {
  lineSequence += 1;
  const unit = line?.unit ?? "months";
  const offset = line?.unit === undefined ? line?.offsetMonths : line?.offset;
  return { lineId: `line-${lineSequence}`, unit, offset, percentBps: line?.percentBps };
};

export const seedLines = (profile) => (
  Array.isArray(profile) && profile.length > 0 ? profile : AT_SIGHT_PROFILE
).map(withLineId);

const sumOf = (lines) => lines.reduce(
  (total, line) => total + (Number.isFinite(line.percentBps) ? line.percentBps : 0),
  0,
);

// O valor de cada parcela: com a forma completa (soma 100%), a MESMA repartição por maior
// resto do motor, então as parcelas fecham no centavo com o valor global. Forma ainda em
// digitação: a leitura aproximada de cada linha.
const lineCentsOf = (totalCents, lines) => {
  const bps = lines.map((line) => line.percentBps);
  const complete = bps.every((value) => Number.isSafeInteger(value) && value > 0)
    && bps.reduce((total, value) => total + value, 0) === 10000;
  if (complete) return allocateCents(totalCents, bps);
  return bps.map((value) => (Number.isFinite(value) ? Math.round((totalCents * value) / 10000) : null));
};

const RuleProfileTable = ({
  lines,
  editable,
  message,
  summary,
  onChangeLine,
  onRemoveLine,
  onAddLine,
  // Valor global do alvo (centavos). Com ele, a tabela mostra quanto cada parcela leva.
  totalCents = null,
  // Início do novo cronograma ("YYYY-MM"): com ele, cada linha mostra a competência em que cai.
  startMonth = null,
  // Mostra as parcelas mesmo com valor zero: a regra que mantém as regras dos insumos e
  // não sobra nada para ela distribuir (D19) tem de dizer R$ 0,00, não esconder a coluna.
  showZeroTotal = false,
}) => {
  const errorId = message ? PROFILE_ERROR_ID : undefined;
  const showValue =
    Number.isFinite(totalCents) &&
    (totalCents > 0 || (showZeroTotal && totalCents === 0));
  const competences = lineCompetences(lines, startMonth);
  const installmentCents = showValue ? lineCentsOf(totalCents, lines) : [];
  const showCompetence = competences.some(Boolean);
  const total = sumOf(lines);
  return (
    <div className="ampli-rule-editor__profile">
      <div
        className="ampli-rule-editor__table"
        role="region"
        aria-label="Perfil da regra"
        tabIndex={0}
      >
        <table aria-label="Percentual por defasagem">
          <thead>
            <tr>
              <th scope="col">Parcela</th>
              {showCompetence ? <th scope="col">Competência</th> : null}
              <th scope="col">Defasagem do início</th>
              <th scope="col">Unidade</th>
              <th scope="col">Percentual</th>
              {showValue ? <th className="ampli-rule-editor__value-col">Valor da parcela</th> : null}
              {editable ? <th>Ações</th> : null}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const range = offsetRangeFor(line.unit);
              return (
                <tr key={line.lineId} data-line-id={line.lineId}>
                  <td className="ampli-rule-editor__installment">{`${index + 1}ª`}</td>
                  {showCompetence ? (
                    <td className="ampli-rule-editor__competence">
                      {competences[index] ? competenceText(competences[index]) : "—"}
                    </td>
                  ) : null}
                  <td>
                    {editable ? (
                      <input
                        type="number"
                        className="default-input"
                        aria-label={`Defasagem da linha ${index + 1}`}
                        min={String(range.min)}
                        max={String(range.max)}
                        value={Number.isFinite(line.offset) ? line.offset : ""}
                        onChange={(event) => onChangeLine(line.lineId, {
                          offset: integerInput(event.target.value),
                        })}
                      />
                    ) : line.offset}
                  </td>
                  <td>
                    {editable ? (
                      <select
                        className="default-input"
                        aria-label={`Unidade da linha ${index + 1}`}
                        value={line.unit}
                        onChange={(event) => onChangeLine(line.lineId, { unit: event.target.value })}
                      >
                        {UNIT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : unitLabel(line.unit)}
                  </td>
                  <td>
                    {editable ? (
                      <input
                        type="number"
                        className="default-input"
                        aria-label={`Percentual da linha ${index + 1}`}
                        aria-describedby={errorId}
                        value={bpsToPercentInput(line.percentBps)}
                        onChange={(event) => onChangeLine(line.lineId, {
                          percentBps: percentInputToBps(event.target.value),
                        })}
                      />
                    ) : percentLabel(line.percentBps)}
                  </td>
                  {showValue ? (
                    <td className="ampli-rule-editor__value-col">
                      <TargetValue cents={installmentCents[index]} />
                    </td>
                  ) : null}
                  {editable ? (
                    <td>
                      <button
                        type="button"
                        className="button button-ghost small"
                        aria-label={`Remover linha ${index + 1}`}
                        onClick={() => onRemoveLine(line.lineId)}
                      >
                        Remover
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="ampli-rule-editor__profile-footer">
        <p
          className={`ampli-rule-editor__sum${total === 10000 ? " is-complete" : ""}`}
          aria-live="polite"
        >
          {`Soma: ${percentLabel(total)}`}
        </p>
        {!message && summary ? (
          <p className="ampli-rule-editor__summary">{summary}</p>
        ) : null}
        {editable ? (
          <button
            type="button"
            className="button button-outline small"
            disabled={lines.length >= MAX_LINES}
            onClick={onAddLine}
          >
            Adicionar linha
          </button>
        ) : null}
      </div>
      {message ? (
        <p className="alert alert-error" role="alert" id={PROFILE_ERROR_ID}>{message}</p>
      ) : null}
    </div>
  );
};

export default RuleProfileTable;
